import { PregnancyDiagnosisParams, CoverageProcessParams } from 'src/domain/coverage-process'
import { PregnancyDiagnosisDto } from 'src/dto/pregnancy-diagnosis.dto'
import {
  PregnancyDiagnosisService,
  SaveDiagnosisWithBirthParams,
  SaveDiagnosisCompleteParams
} from 'src/services/api/pregnancy-diagnosis.service'
import { IAnimal } from 'src/dto/animal.dto'
import { CoverageProcessDto } from 'src/dto/coverage-process.dto'
import { MontaTypes } from 'src/dto/couple-planning.dto'

/**
 * Controller de diagnósticos de prenhez
 * Camada intermediária entre hooks/UI e serviços
 * Responsável por orquestrar chamadas de serviço e transformar dados quando necessário
 */
export class PregnancyDiagnosisController {
  private service: PregnancyDiagnosisService

  constructor() {
    this.service = new PregnancyDiagnosisService()
  }

  // ========== Métodos de Leitura ==========

  public async getPregnancyDiagnosisListByFarmId(farmId: string): Promise<PregnancyDiagnosisDto[]> {
    const response = await this.service.getPregnancyDiagnosisListByFarmId(farmId)
    return response.data.filter((item) => item.acao !== 'Removido')
  }

  public async getPregnancyDiagnosisById(farmId: string, id: string): Promise<PregnancyDiagnosisDto> {
    const response = await this.service.getPregnancyDiagnosisById(farmId, id)
    return response.data
  }

  public async getPregnancyDiagnosisListByAnimalId(farmId: string, animalId: string): Promise<PregnancyDiagnosisDto[]> {
    const response = await this.service.getPregnancyDiagnosisListByAnimalId(farmId, animalId)
    return response.data
  }

  // ========== Métodos de Escrita Básicos ==========

  public async createPregnancyDiagnosis(
    farmId: string,
    pregnancyDiagnosisDto: PregnancyDiagnosisParams
  ): Promise<PregnancyDiagnosisDto> {
    const response = await this.service.createPregnancyDiagnosis(farmId, pregnancyDiagnosisDto)
    return response.data
  }

  public async updatePregnancyDiagnosis(
    farmId: string,
    pregnancyDiagnosisDto: PregnancyDiagnosisParams
  ): Promise<PregnancyDiagnosisDto> {
    const response = await this.service.updatePregnancyDiagnosis(farmId, pregnancyDiagnosisDto)
    return response.data
  }

  // ========== Métodos de Orquestração (Operações Compostas) ==========

  /**
   * Salva diagnóstico completo incluindo criação de animais e atualização de processo
   * Este método orquestra toda a operação através da camada de serviço
   *
   * @param farmId - ID da fazenda
   * @param params - Parâmetros completos incluindo diagnóstico, processo e nascimentos
   * @returns Resultado completo com diagnóstico, animais criados e processo atualizado
   */
  public async saveDiagnosisComplete(
    farmId: string,
    params: SaveDiagnosisCompleteParams
  ): Promise<{
    diagnosis: PregnancyDiagnosisDto
    createdAnimals?: IAnimal[]
    updatedProcess?: CoverageProcessDto
  }> {
    return await this.service.saveDiagnosisComplete(farmId, params)
  }

  /**
   * Atualiza diagnóstico com informações de nascimento
   * Método simplificado para quando só precisa atualizar diagnóstico e criar filhotes
   *
   * @param farmId - ID da fazenda
   * @param diagnosis - Dados do diagnóstico incluindo nascimentos
   * @returns Diagnóstico atualizado e animais criados
   */
  public async updateDiagnosisWithBirth(
    farmId: string,
    diagnosis: SaveDiagnosisWithBirthParams,
    username: string
  ): Promise<{
    diagnosis: PregnancyDiagnosisDto
    createdAnimals?: IAnimal[]
  }> {
    return await this.service.updateDiagnosisWithBirth(farmId, diagnosis, username)
  }

  // ========== Métodos de Criação de Casais ==========

  /**
   * Cria um novo casal (diagnóstico + processo de cobertura)
   *
   * @param farmId - ID da fazenda
   * @param diagnosisData - Dados do diagnóstico
   * @param coverageData - Dados do processo de cobertura
   * @returns Diagnóstico e processo criados
   */
  public async createCouple(
    farmId: string,
    diagnosisData: PregnancyDiagnosisParams,
    coverageData: CoverageProcessParams
  ): Promise<{
    diagnosis: PregnancyDiagnosisDto
    coverageProcess: CoverageProcessDto
  }> {
    return await this.service.createCouple(farmId, diagnosisData, coverageData)
  }

  /**
   * Cria múltiplos casais em lote
   *
   * @param farmId - ID da fazenda
   * @param animals - Lista de animais
   * @param commonData - Dados comuns para todos os casais
   * @returns Lista de casais criados
   */
  public async createMultipleCouples(
    farmId: string,
    animals: IAnimal[],
    commonData: {
      idTouro: string
      dataInicio: string
      dataFim?: string | null
      tipoMonta: MontaTypes
      inseminador?: string
      dosagem?: string
    }
  ): Promise<
    Array<{
      animal: IAnimal
      diagnosis: PregnancyDiagnosisDto
      coverageProcess: CoverageProcessDto
    }>
  > {
    return await this.service.createMultipleCouples(farmId, animals, commonData)
  }
}
